import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Card,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LayersIcon from '@mui/icons-material/Layers';
import { BASE_URL } from '../constants';
// import { BASE_URL } from '../../constants';

function HouseList() {
  const { projectId } = useParams();
  const [houses, setHouses] = useState([]);
  const [project, setProject] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    floor: '',
    house_id: '',
  });
  const navigate = useNavigate();

  // Fetch project details
  useEffect(() => {
    axios
      .get(`${BASE_URL}projects/${projectId}/`)
      .then((response) => setProject(response.data))
      .catch((error) => console.error('Error fetching project:', error));
  }, [projectId]);

  // Fetch houses for the project
  useEffect(() => {
    axios
      .get(`${BASE_URL}houses/?project=${projectId}`)
      .then((response) => {
        // Sort houses by floor and house_id
        const sortedHouses = response.data.sort((a, b) => {
          if (a.floor !== b.floor) {
            return a.floor - b.floor;
          }
          return a.house_id.localeCompare(b.house_id);
        });
        setHouses(sortedHouses);
      })
      .catch((error) => console.error('Error fetching houses:', error));
  }, [projectId]);

  // Group houses by floor
  const groupHousesByFloor = (houses) => {
    const grouped = houses.reduce((acc, house) => {
      const floor = house.floor;
      if (!acc[floor]) {
        acc[floor] = [];
      }
      acc[floor].push(house);
      return acc;
    }, {});
    // Sort floors numerically
    return Object.keys(grouped)
      .sort((a, b) => Number(a) - Number(b))
      .reduce((acc, key) => {
        // Sort houses within each floor by house_id
        acc[key] = grouped[key].sort((a, b) => a.house_id.localeCompare(b.house_id));
        return acc;
      }, {});
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const houseData = {
      project_id: projectId,
      floor: parseInt(formData.floor, 10),
      house_id: formData.house_id,
    };

    axios
      .post(`${BASE_URL}houses/`, houseData)
      .then((response) => {
        const updatedHouses = [...houses, response.data].sort((a, b) => {
          if (a.floor !== b.floor) {
            return a.floor - b.floor;
          }
          return a.house_id.localeCompare(b.house_id);
        });
        setHouses(updatedHouses);
        setShowForm(false);
        resetForm();
      })
      .catch((error) => console.error('Error adding house:', error));
  };

  // Reset form fields
  const resetForm = () => {
    setFormData({
      floor: '',
      house_id: '',
    });
  };

  // Floor options (0 to 29)
  const floorOptions = Array.from({ length: 30 }, (_, i) => i);

  const groupedHouses = groupHousesByFloor(houses);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: { xs: '16px', md: '32px' },
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        maxWidth: '1200px',
        margin: '0 auto',
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: '700', color: '#1a237e', mb: '24px' }}
      >
        Houses for {project ? `${project.name} (${project.area})` : 'Project'}
      </Typography>

      {/* Add House Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setShowForm(true);
          resetForm();
        }}
        sx={{
          mb: '24px',
          textTransform: 'none',
          padding: '10px 20px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        Add House
      </Button>

      {/* House Form */}
      {showForm && (
        <Paper
          sx={{
            padding: '24px',
            mb: '32px',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            backgroundColor: '#fafafa',
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Floor</InputLabel>
              <Select
                name="floor"
                value={formData.floor}
                onChange={handleInputChange}
                required
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="">Select Floor</MenuItem>
                {floorOptions.map((floor) => (
                  <MenuItem key={floor} value={floor}>
                    {floor === 0 ? 'Ground Floor' : `Floor ${floor}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="House ID"
              name="house_id"
              value={formData.house_id}
              onChange={handleInputChange}
              fullWidth
              required
              placeholder="e.g., A101"
              sx={{ mb: 3, '& .MuiInputBase-root': { borderRadius: '8px' } }}
            />

            <Box sx={{ display: 'flex', gap: '12px' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ textTransform: 'none', borderRadius: '8px', padding: '10px 20px' }}
              >
                Add House
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                sx={{ textTransform: 'none', borderRadius: '8px', padding: '10px 20px' }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* House List by Floor */}
      {houses.length === 0 ? (
        <Card
          sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            p: '24px',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" color="textSecondary">
            No houses found. Click "Add House" to create a new house.
          </Typography>
        </Card>
      ) : (
        Object.keys(groupedHouses).map((floor) => (
          <Accordion
            key={floor}
            sx={{
              mb: '16px',
              borderRadius: '12px !important',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#fafafa',
                borderRadius: '12px',
                padding: '0 16px',
                '& .MuiAccordionSummary-content': { alignItems: 'center' },
              }}
            >
              <LayersIcon sx={{ mr: 1, color: (theme) => theme.palette.primary.main }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: '600', color: (theme) => theme.palette.primary.main }}
              >
                {floor === '0' ? 'Ground Floor' : `Floor ${floor}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5' }}>
                        House ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: '600', backgroundColor: '#f5f5f5' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedHouses[floor].map((house) => (
                      <TableRow
                        key={house.id}
                        sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                      >
                        <TableCell>{house.house_id}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => navigate(`/houses/${house.id}/tasks`)}
                            sx={{
                              textTransform: 'none',
                              borderRadius: '8px',
                              padding: '6px 12px',
                            }}
                          >
                            View Tasks
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
}

export default HouseList;